import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import {
  ReceiptRefundIcon,
  BuildingStorefrontIcon,
  ClockIcon,
  ArrowRightIcon,
  BanknotesIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import orderApi from '../../../services/orderApi';
import NoOrders from '../../../assets/no-orders.svg';
import { UserRoles } from '../../../data/enums';
import {
  PageHeader,
  StatCard,
  StatusBadge,
  UserAvatar,
  TWDataTable,
  TWPagination,
  EmptyState,
} from '../../../components/ui';
import { headerCls, DataTableSkeleton } from '../../../data/columns';
import { formatCurrency } from '../../../utils/baseUtils';
import './refund-approvals.css';

const cn = (...c) => c.filter(Boolean).join(' ');

const RefundApprovals = () => {
  const navigate = useNavigate();
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const userRoles = useSelector((state) => state.authReducer.roles);
  const isAdmin = userRoles.includes(UserRoles.ADMIN);

  const [orderList, setOrderList] = useState([]);
  const [loading, setLoading] = useState({ listLoading: true });
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 20,
    totalRecords: 0,
  });

  const getRefundApprovalOrders = async () => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    try {
      const companyId = globalCompany?.id;
      const query = {
        PageNumber: pagination.pageNumber,
        PageSize: pagination.pageSize,
        deleted: false,
        hasrefundapproval: true,
        companyid: isAdmin ? '' : companyId ? companyId : '',
      };
      const list = await orderApi.list(query);
      setOrderList(list?.data?.data || []);
      setPagination({
        pageNumber: list?.data?.pageNumber || 1,
        pageSize: list?.data?.pageSize || 20,
        totalRecords: list?.data?.totalRecords || 0,
      });
    } catch (error) {
      setOrderList([]);
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  useEffect(() => {
    getRefundApprovalOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageNumber, pagination.pageSize, globalCompany?.id]);

  const onClickRow = (id) => navigate(`/dashboard/orders/${id}`);

  const totalRefundValue = useMemo(
    () =>
      orderList.reduce(
        (sum, o) => sum + (o.refundapproval?.amount || o.refund || 0),
        0,
      ),
    [orderList],
  );

  const formatOrderTime = (order) => {
    const m = order?.store?.timezoneid
      ? moment(order.createdate).tz(order.store.timezoneid)
      : moment(order.createdate).utcOffset((order.timezone || 0) * 60);
    return { time: m.format('hh:mm A'), date: m.format('MMM D, YYYY') };
  };

  const hasOrders = orderList.length > 0;

  const headerBadge =
    hasOrders && !loading.listLoading ? (
      <span className="inline-flex w-fit items-center gap-2 rounded-full border border-amber-200/70 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-300">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-70" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
        </span>
        {pagination.totalRecords} pending review
      </span>
    ) : null;

  return (
    <div className="refund-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          title="Refund Approvals"
          description="Review and resolve orders that are awaiting refund approval. Click a row to open the full order and approve or decline."
          actions={headerBadge}
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <StatCard
            icon={ReceiptRefundIcon}
            tone="amber"
            label="Awaiting Approval"
            value={
              loading.listLoading
                ? '—'
                : Number(pagination.totalRecords).toLocaleString('en-US')
            }
          />
          <StatCard
            icon={BanknotesIcon}
            tone="rose"
            label="Refund Value On This Page"
            value={loading.listLoading ? '—' : formatCurrency(totalRefundValue)}
          />
        </div>

        {loading.listLoading ? (
          <DataTableSkeleton
            rows={6}
            headers={[
              { label: 'Receipt', w: 'w-28', shape: 'avatar' },
              isAdmin && { label: 'Company', w: 'w-32' },
              { label: 'Customer', w: 'w-32', shape: 'avatar' },
              { label: 'Store', w: 'w-32' },
              { label: 'Requested', w: 'w-24' },
              { label: 'Status', w: 'w-24', shape: 'pill' },
              { label: 'Refund', align: 'right', w: 'w-20' },
            ].filter(Boolean)}
          />
        ) : !hasOrders ? (
          <EmptyState
            image={NoOrders}
            title="Nothing to review"
            description="All refund requests have been resolved. New approvals will appear here as they come in."
          />
        ) : (
          <>
            <div className="reveal hidden lg:block">
              <TWDataTable
                columns={[
                  {
                    title: <span className={headerCls}>Receipt</span>,
                    key: 'receipt',
                    render: (_, order) => (
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-slate-900/90 text-[0.7rem] font-bold text-white shadow-sm dark:bg-slate-700">
                          #
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-bold tabular-nums text-slate-900 dark:text-slate-100">
                            {order.ordernumberstr || '—'}
                          </div>
                          <div className="truncate text-xs text-slate-400 dark:text-slate-500">
                            Order #{order.orderid || '—'}
                          </div>
                        </div>
                      </div>
                    ),
                  },
                  isAdmin && {
                    title: <span className={headerCls}>Company</span>,
                    key: 'company',
                    render: (_, order) => (
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {order.store?.company?.name || '—'}
                      </span>
                    ),
                  },
                  {
                    title: <span className={headerCls}>Customer</span>,
                    key: 'customer',
                    render: (_, order) => {
                      const customerName =
                        order.name || order.customer?.fullname || 'Guest';
                      return (
                        <div className="flex min-w-0 items-center gap-2.5">
                          <UserAvatar name={customerName} size="sm" />
                          <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                            {customerName}
                          </span>
                        </div>
                      );
                    },
                  },
                  {
                    title: <span className={headerCls}>Store</span>,
                    key: 'store',
                    render: (_, order) => (
                      <div className="flex min-w-0 items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
                        <BuildingStorefrontIcon className="h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500" />
                        <span className="truncate">
                          {order.store?.name || '—'}
                        </span>
                      </div>
                    ),
                  },
                  {
                    title: <span className={headerCls}>Requested</span>,
                    key: 'requested',
                    render: (_, order) => {
                      const when = formatOrderTime(order);
                      return (
                        <div>
                          <div className="flex items-center gap-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                            <ClockIcon className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
                            <span className="tabular-nums">{when.time}</span>
                          </div>
                          <div className="text-xs text-slate-400 dark:text-slate-500">
                            {when.date}
                          </div>
                        </div>
                      );
                    },
                  },
                  {
                    title: <span className={headerCls}>Status</span>,
                    key: 'status',
                    render: () => (
                      <StatusBadge tone="amber" label="Pending Review" dot />
                    ),
                  },
                  {
                    title: (
                      <span className={`${headerCls} block text-right`}>
                        Refund
                      </span>
                    ),
                    key: 'refund',
                    align: 'right',
                    render: (_, order) => {
                      const refundAmount =
                        order.refundapproval?.amount || order.refund || 0;
                      return (
                        <div className="flex items-center justify-end gap-3">
                          <div className="text-right">
                            <div className="tabular-nums text-base font-bold text-rose-600 dark:text-rose-400">
                              {formatCurrency(refundAmount)}
                            </div>
                            <div className="text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                              Requested
                            </div>
                          </div>
                          <ChevronRightIcon className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                        </div>
                      );
                    },
                  },
                ].filter(Boolean)}
                data={orderList}
                rowKey={(order, idx) => order.id || idx}
                rowClickPath={null}
                onRowClick={(order) => onClickRow(order.id)}
                pagination={
                  pagination.totalRecords > pagination.pageSize
                    ? {
                        showSizeChanger: true,
                        current: pagination.pageNumber,
                        pageSize: pagination.pageSize,
                        total: pagination.totalRecords,
                        showTotal: (total, range) =>
                          `Showing ${range[0]} to ${range[1]} of ${total} approvals`,
                        onChange: (page, pageSize) =>
                          setPagination((prev) => ({
                            ...prev,
                            pageNumber: page,
                            pageSize,
                          })),
                      }
                    : undefined
                }
              />
            </div>

            <div className="space-y-3 lg:hidden">
              {orderList.map((order, i) => {
                const when = formatOrderTime(order);
                const customerName =
                  order.name || order.customer?.fullname || 'Guest';
                const refundAmount =
                  order.refundapproval?.amount || order.refund || 0;
                return (
                  <button
                    key={order.id || i}
                    onClick={() => onClickRow(order.id)}
                    className="refund-row reveal group w-full rounded-md border border-slate-200 bg-white p-4 text-left shadow-sm hover:border-blue-300 hover:shadow-md dark:border-slate-700 dark:bg-slate-800/70 dark:hover:border-sky-500/40 sm:p-5"
                    style={{ animationDelay: `${i * 40}ms` }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <UserAvatar name={customerName} size="md" />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {customerName}
                          </div>
                          <div className="truncate text-xs text-slate-400 dark:text-slate-500">
                            Receipt{' '}
                            <span className="tabular-nums font-semibold text-slate-500 dark:text-slate-400">
                              {order.ordernumberstr || '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="tabular-nums text-lg font-bold text-rose-600 dark:text-rose-400">
                          {formatCurrency(refundAmount)}
                        </div>
                        <div className="text-[0.6rem] font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                          Refund
                        </div>
                      </div>
                    </div>

                    {isAdmin && order.store?.company?.name && (
                      <div className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
                        {order.store.company.name}
                      </div>
                    )}

                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500 dark:text-slate-400">
                      <div className="flex items-center gap-1.5">
                        <BuildingStorefrontIcon className="h-3.5 w-3.5 text-slate-400" />
                        <span className="truncate">
                          {order.store?.name || '—'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <ClockIcon className="h-3.5 w-3.5 text-slate-400" />
                        <span className="tabular-nums">
                          {when.time} · {when.date}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-2">
                      <StatusBadge tone="amber" label="Pending Review" dot />
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-sky-400">
                        Review
                        <ArrowRightIcon className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="reveal flex flex-col items-center justify-between gap-3 px-4 py-3 text-sm sm:flex-row sm:px-5 lg:hidden">
              <p className="text-slate-500 dark:text-slate-400">
                Showing{' '}
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {(pagination.pageNumber - 1) * pagination.pageSize + 1}
                </span>{' '}
                –{' '}
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {Math.min(
                    pagination.pageNumber * pagination.pageSize,
                    pagination.totalRecords,
                  )}
                </span>{' '}
                of{' '}
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {pagination.totalRecords}
                </span>
              </p>
              <TWPagination
                showSizeChanger
                size="small"
                onChange={(page, pageSize) =>
                  setPagination((prev) => ({
                    ...prev,
                    pageNumber: page,
                    pageSize,
                  }))
                }
                pageSize={pagination.pageSize}
                current={pagination.pageNumber}
                total={pagination.totalRecords}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RefundApprovals;
